import { ECOSYSTEM_SERVICES } from "@iec-id/shared/constants";
import { buttonSmVariants, cn, Icon, type IconName, IconTile, icons } from "@iec-id/ui";
import { serviceLogoSrc } from "@/components/signin/service-logo";
import { useInviteT } from "./invite-i18n";

/**
 * Сервисы экосистемы в правой колонке лендинга приглашения (макет 4c).
 *
 * ВСЕ, а не выборка из четырёх (решение владельца 22.08.2026): человек, которого
 * позвали, решает, стоит ли заводить аккаунт, — и половина ответа была скрыта.
 * Список берётся из реестра, поэтому новый сервис приезжает сюда сам.
 *
 * ── ПОЧЕМУ СТРОКА, А НЕ КАРТОЧКА С ПОЛНЫМ ОПИСАНИЕМ ───────────────────────
 *
 * Колонка шириной 320px, описание в ней разваливалось на четыре строки, и
 * восемь сервисов растягивали страницу вдвое против карточки регистрации
 * слева — то есть форма, ради которой человек пришёл, уезжала за экран, а
 * дочитать список до конца всё равно никто не станет. Описание осталось, но
 * ограничено двумя строками (`line-clamp-2`): смысл сервиса из них понятен,
 * высота строки перестала зависеть от длины текста, и плитки стали одной
 * высоты.
 *
 * Строка кликается ЦЕЛИКОМ, как плитки сервисов в витрине: «Открыть» —
 * `<span>`, а не вложенная ссылка (ссылка внутри ссылки — невалидная
 * разметка, и браузер разбирает её непредсказуемо). Клики она не принимает
 * (`pointer-events-none`), а на ховере всей строки зеленеет.
 */
export function ServiceTiles({ highlightId }: { highlightId?: string }) {
	const t = useInviteT();
	const services = ECOSYSTEM_SERVICES.filter((service) => service.id !== "iec-id");

	return (
		<ul aria-label={t("services.listAria")} className="flex flex-col gap-1">
			{services.map((service) => {
				// Глиф из реестра может не существовать в наборе (сервисы добавляют
				// раньше иконок) — тогда плитка остаётся, а глиф становится общим.
				const icon: IconName = service.icon in icons ? (service.icon as IconName) : "LayoutGrid";
				const highlighted = highlightId === service.id;
				const description = t(`services.description.${service.id}`, { defaultValue: "" });
				const logo = serviceLogoSrc(service.id);

				return (
					<li key={service.id}>
						<a
							href={service.url}
							target="_blank"
							rel="noreferrer"
							className={cn(
								"iec-press-glyph group/service flex flex-col gap-1.5 rounded-card p-3",
								// Имя сервиса всегда `text-fg`: глобальный `a { color }`
								// иначе красит подпись зелёным.
								"text-fg no-underline hover:text-fg focus-ring",
								"transition-colors duration-150 ease-smooth",
								highlighted ? "bg-chip-brand" : "bg-card hover:bg-sunken",
							)}
						>
							<span className="flex w-full min-w-0 items-center gap-3">
								<IconTile size={40} tone={highlighted ? "card" : "sunken"}>
									{logo ? (
										// Знак узнаётся с одного взгляда, глиф только называет
										// тип; медиа на шаг туже контейнера (r10 → r8).
										<img src={logo} alt="" aria-hidden="true" className="size-5 rounded-sm" />
									) : (
										<Icon name={icon} size={20} />
									)}
								</IconTile>
								<span className="t-row min-w-0 flex-grow break-words">{service.name}</span>
								<span
									className={cn(
										buttonSmVariants({ onGrey: highlighted }),
										"pointer-events-none shrink-0 whitespace-nowrap",
										"transition-colors duration-150 ease-smooth group-hover/service:bg-brand",
									)}
								>
									{t("services.open")}
								</span>
							</span>
							{description ? (
								<span className="t-caption line-clamp-2 min-w-0 break-words text-fg-muted">
									{description}
								</span>
							) : null}
						</a>
					</li>
				);
			})}
		</ul>
	);
}
